import { db } from '../firebase';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  query,
  where,
  serverTimestamp,
  deleteDoc,
} from 'firebase/firestore';
import type { CoachOffering, OfferingType } from '../types/lesson';

const KNOWN_OFFERING_TYPES: OfferingType[] = [
  'shot_consult',
  'single_lesson',
  'training_program',
  'free_intro_call',
  'single_live_lesson',
  'live_membership',
];

/** Normalize legacy Firestore offering docs into the current CoachOffering shape. */
function normalizeOffering(data: Record<string, any>, id: string): CoachOffering {
  // Infer offeringType from available fields
  let offeringType: OfferingType = data.offeringType;
  if (!offeringType || !KNOWN_OFFERING_TYPES.includes(offeringType)) {
    if (data.totalLessons || /program|package|bundle/i.test(data.title || '')) {
      offeringType = 'training_program';
    } else if (data.price === 0 || /shot|consult/i.test(data.title || '')) {
      offeringType = 'shot_consult';
    } else {
      offeringType = 'single_lesson';
    }
  }

  // Derive deliveryMode from offeringType when absent
  const deliveryMode: 'async' | 'live' =
    data.deliveryMode ?? (
      offeringType === 'free_intro_call' ||
      offeringType === 'single_live_lesson' ||
      offeringType === 'live_membership'
        ? 'live'
        : 'async'
    );

  // Normalize price: legacy docs may use priceValue instead of price
  const price: number = typeof data.price === 'number'
    ? data.price
    : typeof data.priceValue === 'number'
      ? data.priceValue
      : 0;

  return {
    ...data,
    id,
    offeringType,
    deliveryMode,
    price,
    totalLessons: data.totalLessons ?? (offeringType === 'training_program' ? 4 : undefined),
    durationMinutes: data.durationMinutes ?? (
      offeringType === 'free_intro_call' ? 15 :
      offeringType === 'single_live_lesson' || offeringType === 'live_membership' ? 45 :
      undefined
    ),
    createdAt: data.createdAt?.toDate?.().toISOString?.() || data.createdAt || new Date().toISOString(),
  } as CoachOffering;
}

/**
 * Creates a new coach offering
 */
export async function createCoachOffering(
  coachId: string,
  offeringData: Omit<CoachOffering, 'id' | 'coachId' | 'createdAt'>
): Promise<string> {
  const offeringRef = doc(collection(db, 'coachOfferings'));
  const offeringId = offeringRef.id;

  const offering: CoachOffering = {
    id: offeringId,
    coachId,
    ...offeringData,
    createdAt: new Date().toISOString(),
  };

  await setDoc(offeringRef, {
    ...offering,
    createdAt: serverTimestamp(),
  });

  return offeringId;
}

/**
 * Gets all offerings for a specific coach
 */
export async function getCoachOfferings(coachId: string): Promise<CoachOffering[]> {
  const q = query(
    collection(db, 'coachOfferings'),
    where('coachId', '==', coachId)
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => normalizeOffering(d.data(), d.id));
}

/**
 * Gets active offerings for a specific coach (for display on profile)
 */
export async function getActiveCoachOfferings(coachId: string): Promise<CoachOffering[]> {
  const offerings = await getCoachOfferings(coachId);
  return offerings.filter(offering => offering.isActive);
}

/**
 * Gets a single offering by ID
 */
export async function getCoachOffering(offeringId: string): Promise<CoachOffering | null> {
  const offeringRef = doc(db, 'coachOfferings', offeringId);
  const offeringSnap = await getDoc(offeringRef);

  if (!offeringSnap.exists()) {
    return null;
  }

  return normalizeOffering(offeringSnap.data(), offeringSnap.id);
}

/**
 * Updates an existing coach offering
 */
export async function updateCoachOffering(
  offeringId: string,
  updates: Partial<Omit<CoachOffering, 'id' | 'coachId' | 'createdAt'>>
): Promise<void> {
  const offeringRef = doc(db, 'coachOfferings', offeringId);
  await updateDoc(offeringRef, {
    ...updates,
    updatedAt: serverTimestamp(),
  });
}

/**
 * Toggles the active status of an offering
 */
export async function toggleOfferingStatus(
  offeringId: string,
  isActive: boolean
): Promise<void> {
  await updateCoachOffering(offeringId, { isActive });
}

/**
 * Deletes a coach offering
 */
export async function deleteCoachOffering(offeringId: string): Promise<void> {
  const offeringRef = doc(db, 'coachOfferings', offeringId);
  await deleteDoc(offeringRef);
}

/**
 * Creates a default async lesson offering for a new coach
 * @deprecated Use createDefaultOfferings instead
 */
export async function createDefaultOffering(coachId: string): Promise<string> {
  return createCoachOffering(coachId, {
    title: 'Asynchronous Lesson',
    description: 'Upload your tennis video and receive personalized feedback',
    offeringType: 'single_lesson',
    price: 5000,
    isActive: true,
  });
}

/**
 * Creates the three default lesson offerings for a new coach.
 * Prices reflect the coach's lessonPrice set during onboarding (passed in as priceCents).
 * Shot consults are always free (price: 0).
 */
export async function createDefaultOfferings(coachId: string, priceCents = 8500): Promise<string[]> {
  const offerings = [
    {
      title: 'Shot Consultation',
      description: '<p>Upload a short video of <b>one shot</b> (serve, forehand, backhand, or volley). Your coach delivers a quick video breakdown with <b>clear corrections and drills</b> to improve that shot.</p>',
      offeringType: 'shot_consult' as const,
      price: 0,
      isActive: true,
    },
    {
      title: 'Full Video Analysis',
      description: '<p>Upload match play or practice footage. Your coach analyzes your full game and delivers a <b>video breakdown of the key issues limiting your performance</b>, with clear demonstrations and drills to fix them.</p>',
      offeringType: 'single_lesson' as const,
      price: priceCents,
      isActive: true,
    },
    {
      title: '4 Lesson Program',
      description: `<p><b>Risk-free package. If you're not satisfied after the first lesson, we'll refund the full amount back.</b></p>
<p>Work with your coach over multiple lessons to fix the key issues in your game and build lasting improvement. Upload a new video for each lesson and receive ongoing corrections, drills, and clear progress across lessons.</p>`,
      offeringType: 'training_program' as const,
      price: priceCents,
      totalLessons: 4,
      isActive: true,
    },
  ];

  const offeringIds: string[] = [];
  for (const offeringData of offerings) {
    const id = await createCoachOffering(coachId, offeringData);
    offeringIds.push(id);
  }

  return offeringIds;
}
